import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { eventsRequest } from '../utils/eventsRequest';
import {
  SESSION_KEY_LOGO_URL,
  SESSION_KEY_COMPANY_NAME,
  SESSION_KEY_WEBSITE,
  SESSION_KEY_PHONE,
  SESSION_KEY_EMAIL,
  SESSION_KEY_ADDRESS,
  SESSION_KEY_COUNTRY_CODE,
  SESSION_KEY_POSTAL,
  SESSION_KEY_HERO_TAGLINE,
} from '../constants';

interface PublicSettingsResponse {
  data: {
    companyName: string;
    logoUrl: string;
    website: string;
    phone: string;
    email: string;
    address: string;
    countryCode: string;
    postal: string;
    heroTagline: string | null;
  };
}

type PublicSettingsData = PublicSettingsResponse['data'];

export const usePublicSettings = () => {
  const { search } = useLocation();
  const getInitialValue = (key: string) => {
    const value = localStorage.getItem(key);
    return value === null ? null : value;
  };

  const [logoUrl, setLogoUrl] = useState<string | null>(() =>
    getInitialValue(SESSION_KEY_LOGO_URL)
  );
  const [companyName, setCompanyName] = useState<string | null>(() =>
    getInitialValue(SESSION_KEY_COMPANY_NAME)
  );
  const [website, setWebsite] = useState<string | null>(() =>
    getInitialValue(SESSION_KEY_WEBSITE)
  );
  const [phone, setPhone] = useState<string | null>(() =>
    getInitialValue(SESSION_KEY_PHONE)
  );
  const [email, setEmail] = useState<string | null>(() =>
    getInitialValue(SESSION_KEY_EMAIL)
  );
  const [address, setAddress] = useState<string | null>(() =>
    getInitialValue(SESSION_KEY_ADDRESS)
  );
  const [countryCode, setCountryCode] = useState<string | null>(() =>
    getInitialValue(SESSION_KEY_COUNTRY_CODE)
  );
  const [postal, setPostal] = useState<string | null>(() =>
    getInitialValue(SESSION_KEY_POSTAL)
  );
  const [heroTagline, setHeroTagline] = useState<string | null>(() =>
    getInitialValue(SESSION_KEY_HERO_TAGLINE)
  );

  useEffect(() => {
    if (companyName) {
      document.title = companyName + ' Events';
    }
  }, [companyName]);

  useEffect(() => {
    let cancelled = false;

    const applyPublicSettings = (data: PublicSettingsData) => {
      const {
        logoUrl: fetchedLogoUrl,
        companyName: fetchedCompanyName,
        website: fetchedWebsite,
        phone: fetchedPhone,
        email: fetchedEmail,
        address: fetchedAddress,
        countryCode: fetchedCountryCode,
        postal: fetchedPostal,
        heroTagline: fetchedHeroTagline,
      } = data;

      const normalizedHeroTagline =
        fetchedHeroTagline != null && fetchedHeroTagline.trim() !== ''
          ? fetchedHeroTagline
          : null;

      localStorage.setItem(SESSION_KEY_LOGO_URL, fetchedLogoUrl);
      localStorage.setItem(SESSION_KEY_COMPANY_NAME, fetchedCompanyName);
      localStorage.setItem(SESSION_KEY_WEBSITE, fetchedWebsite);
      localStorage.setItem(SESSION_KEY_PHONE, fetchedPhone);
      localStorage.setItem(SESSION_KEY_EMAIL, fetchedEmail);
      localStorage.setItem(SESSION_KEY_ADDRESS, fetchedAddress);
      localStorage.setItem(SESSION_KEY_COUNTRY_CODE, fetchedCountryCode);
      localStorage.setItem(SESSION_KEY_POSTAL, fetchedPostal);
      if (normalizedHeroTagline) {
        localStorage.setItem(SESSION_KEY_HERO_TAGLINE, normalizedHeroTagline);
      } else {
        localStorage.removeItem(SESSION_KEY_HERO_TAGLINE);
      }

      setLogoUrl(fetchedLogoUrl);
      setCompanyName(fetchedCompanyName);
      setWebsite(fetchedWebsite);
      setPhone(fetchedPhone);
      setEmail(fetchedEmail);
      setAddress(fetchedAddress);
      setCountryCode(fetchedCountryCode);
      setPostal(fetchedPostal);
      setHeroTagline(normalizedHeroTagline);
    };

    const refreshPublicSettings = async () => {
      try {
        const response = await eventsRequest<PublicSettingsResponse>(
          '/public-settings',
          'get'
        );
        if (cancelled) return;
        if (response.status === 200 || response.status === 201) {
          applyPublicSettings(response.data.data);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to fetch settings:', error);
        }
      }
    };

    refreshPublicSettings();

    return () => {
      cancelled = true;
    };
  }, [search]);

  return {
    logoUrl,
    companyName,
    website,
    phone,
    email,
    address,
    countryCode,
    postal,
    heroTagline,
  };
};
